import { Check, ChevronDown } from "lucide-react";
import {
  Menu,
  MenuItem,
  MenuSection,
  MenuSeparator,
} from "@/components/ui/menu";
import { useWorkspaces } from "@/lib/workspace/queries";
import { createWorkspaceLinks, DIRECTORY_PATH } from "@/lib/workspace/paths";
import type { MeOrg } from "@/lib/auth/types";

/**
 * The active organization, and everything that belongs to it.
 *
 * The name stays readable in the closed trigger: in a shared multi-tenant
 * product the worst available error is acting in the wrong tenant, so the
 * tenant never leaves the screen. Administration hangs off this menu rather
 * than off a global nav, because team and audit configure THIS organization.
 */
export function WorkspaceSwitcher({ active }: { active?: MeOrg }) {
  const organizations = useWorkspaces();
  const links = active ? createWorkspaceLinks(active.slug) : null;
  const current = active?.display_name ?? "Choose organization";

  return (
    <Menu
      label={`Organization: ${current}`}
      trigger={
        <>
          <span className="max-w-[10rem] truncate sm:max-w-[16rem]">
            {current}
          </span>
          <ChevronDown className="size-4 text-fg-subtle" aria-hidden="true" />
        </>
      }
    >
      {active && (
        <>
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium">
              {active.display_name}
            </p>
            <p className="text-[11px] text-fg-subtle">{active.role}</p>
          </div>
          <MenuSeparator />
        </>
      )}

      <MenuSection title="Switch organization">
        {organizations.isPending && (
          <p className="px-2.5 py-2 text-sm text-fg-subtle">
            Loading organizations…
          </p>
        )}
        {organizations.isError && (
          <>
            <p className="px-2.5 py-2 text-sm text-fg-muted">
              Your organizations could not be loaded.
            </p>
            {/* A retry outside a MenuItem is unreachable by keyboard: Tab
                closes the menu and the arrow keys only walk menu items. */}
            <MenuItem
              onSelect={() => {
                void organizations.refetch();
              }}
            >
              Try again
            </MenuItem>
          </>
        )}
        {organizations.data.map((organization) => (
          <OrganizationItem
            key={organization.slug}
            organization={organization}
            current={organization.slug === active?.slug}
          />
        ))}
      </MenuSection>

      {links && (
        <>
          <MenuSeparator />
          <MenuItem to={links.team}>Team and members</MenuItem>
          {/* Omitted for members rather than shown disabled: an owner-only
              control a member cannot use only advertises a door. */}
          {active?.role === "owner" && (
            <MenuItem to={links.audit}>Audit log</MenuItem>
          )}
        </>
      )}

      <MenuSeparator />
      <MenuItem to={DIRECTORY_PATH}>All organizations</MenuItem>
    </Menu>
  );
}

function OrganizationItem({
  organization,
  current,
}: {
  organization: MeOrg;
  current: boolean;
}) {
  const label = (
    <span className="flex min-w-0 items-baseline justify-between gap-2">
      <span className="truncate">{organization.display_name}</span>
      <span className="shrink-0 text-[11px] text-fg-subtle">
        {organization.role}
      </span>
    </span>
  );
  const icon = current ? (
    <Check className="size-4 text-accent" />
  ) : (
    <span className="block size-4" />
  );

  if (current) {
    return <MenuItem icon={icon}>{label}</MenuItem>;
  }

  // The target organization's OWN home, never the current path rewritten
  // across the boundary: that id does not exist on the other side, and a 404
  // immediately after a switch reads as a broken product.
  return (
    <MenuItem icon={icon} to={createWorkspaceLinks(organization.slug).home}>
      {label}
    </MenuItem>
  );
}
